import _startCase from "lodash-es/startCase";
import { useTranslation } from "react-i18next";

import { View } from "services/databases/getViews";
import { useViewNames } from "services/databases/useViewNames";

import { useViewOnly } from "pages/govern/Roles/useViewOnlyContext";

import { useMenu } from "components/ActionMenu/useMenu";
import ContextMenuDivider from "components/ContextMenu/ContextMenuDivider";
import ContextMenuItem from "components/ContextMenu/ContextMenuItem";
import { Menu } from "components/LeftSidebar/RecordMenu/RecordMenu";

import { GrantPrivilegesSelector } from "../PrivilegesTable/GrantPrivilegesSelector";
import { GroupInner } from "../PrivilegesTable/GroupInner";
import { ALL_VIEWS } from "../PrivilegesTable/constant";
import { usePrivilegesHandlers } from "../PrivilegesTable/usePrivilegesHandlers";
import {
  ROLES_WIZARD_ADD_GROUP_EMPTY,
  ROLES_WIZARD_SET_RESOURCE_OBJECTS,
} from "../privilegesReducer";
import { viewPrivileges } from "../types";
import { usePrivilegesState } from "../usePrivilegesContext";

import groupStyles from "../PrivilegesTable/styles.module.scss";
import styles from "./styles.module.scss";

type Props = {
  index: number;
  resource: string;
  catalogName: string;
};

export const ViewsGroup = (props: Props) => {
  const { index, resource, catalogName } = props;
  const { privilegesState, privilegesDispatch } = usePrivilegesState();
  const views = useViewNames({ database: catalogName });
  const isViewOnly = useViewOnly();

  const { onGrantPrivileges, deleteGroup, handleMore } = usePrivilegesHandlers({
    index,
    resource,
    catalogName,
  });

  const viewState = privilegesState.databasePrivileges[catalogName]?.view ?? [
    { toAssign: [], toDeny: [], objects: [] },
  ];
  const groupState = viewState[index];
  const { objects, expanded } = groupState;
  const menu = useMenu();

  const { t } = useTranslation();

  const menuItems = [
    [
      {
        key: "add_group",
        text: t("roles_wizard.privileges.menu.add"),
        action: () => {
          privilegesDispatch({
            type: ROLES_WIZARD_ADD_GROUP_EMPTY,
            resource,
            objects: [{ name: ALL_VIEWS, catalogName }],
            catalogName,
          });
        },
        testId: "roles-wizard-add-views-group",
        disabled: isViewOnly,
      },
    ],
    ...(viewState.length > 1
      ? [
          [
            {
              key: "delete",
              text: t("roles_wizard.privileges.menu.delete"),
              action: deleteGroup,
              testId: "roles-wizard-delete-vies-group",
              disabled: isViewOnly,
            },
          ],
        ]
      : []),
  ];

  const privilegesOptions = viewPrivileges.map(privilege => {
    return {
      value: privilege,
      text: _startCase(privilege.toLowerCase()),
    };
  });

  const selectedViews = [
    ...new Set<string>(objects.map(object => object.name)),
  ];

  const filterBulkResources = (item: { name: string; catalogName: string }) => {
    if (item.catalogName !== catalogName) {
      return true;
    }
    if (item.catalogName === catalogName) {
      return item.name !== ALL_VIEWS;
    }
  };

  const filterDatabaseResources = (item: {
    name: string;
    catalogName: string;
  }) => {
    if (item.catalogName !== catalogName) {
      return true;
    }
    return false;
  };

  const handleBulkResource = (name: string) => {
    const selectedObjects = objects.filter(filterDatabaseResources);

    privilegesDispatch({
      type: ROLES_WIZARD_SET_RESOURCE_OBJECTS,
      resource,
      objects: selectedViews.includes(name)
        ? [...selectedObjects]
        : [...selectedObjects, { catalogName, name }],
      index,
      catalogName,
    });
  };

  const handleResource = (view: { tableName: string }) => {
    const selectedObjects = selectedViews.includes(view.tableName)
      ? objects.filter(object => {
          if (object.catalogName !== catalogName) {
            return true;
          }
          return object.name !== view.tableName;
        })
      : [...objects, { name: view.tableName, catalogName }].filter(
          filterBulkResources
        );

    privilegesDispatch({
      type: ROLES_WIZARD_SET_RESOURCE_OBJECTS,
      resource,
      index,
      catalogName,
      objects: selectedObjects,
    });
  };

  const prefixItems = [
    <ContextMenuItem
      disabled={isViewOnly}
      value={ALL_VIEWS}
      key={ALL_VIEWS}
      checked={selectedViews.includes(ALL_VIEWS)}
      checkedIconPlaceholder={true}
      text={t("roles_wizard.privileges.all_views")}
      direction="left"
      bulkItem
      skipFilter
      onClick={() => {
        handleBulkResource(ALL_VIEWS);
      }}
    />,
    <ContextMenuDivider key="divider" />,
  ];

  const renderItem = (view: View) => {
    return (
      <ContextMenuItem
        disabled={isViewOnly}
        key={view.tableName}
        value={view.tableName}
        text={view.tableName}
        direction="left"
        checked={selectedViews.includes(view.tableName)}
        onClick={() => {
          handleResource(view);
        }}
      />
    );
  };

  const renderCatalogItem = (name: string) => {
    return <span className={styles.resourceName}>{name}</span>;
  };

  return (
    <div className={groupStyles.group}>
      <GroupInner
        expanded={expanded}
        handleMore={handleMore}
        objects={objects}
        items={views}
        prefixItems={prefixItems}
        renderItem={renderItem}
        renderCatalogItem={renderCatalogItem}
        resource={resource}
        index={index}
        searchPlaceholder={t("roles_wizard.privileges.search_view")}
        searchKey="tableName"
      />
      <GrantPrivilegesSelector
        groupState={groupState}
        onChange={onGrantPrivileges}
        privileges={privilegesOptions}
        resource={resource}
        index={index}
      />
      <Menu
        items={menuItems}
        menu={menu}
        testId={`roles-wizard-${resource}-${index}-menu`}
        alwaysVisible
      />
    </div>
  );
};
